// The month/year label above a calendar grid, doubling as a trigger for a
// quick jump-to-month popover — beats scrolling prev/next one month at a time.

import { useEffect, useState } from "react";
import { CompactIconButton, Popover, cx } from "@/platform/ui/primitives/index.js";
import { IconChevronLeft, IconChevronRight } from "@/platform/ui/icons.js";
import { MONTHS_LONG, MONTHS_SHORT } from "@/platform/client";

export function MonthYearPicker({
  cursor,
  onSelect,
  short = false,
}: {
  cursor: Date;
  onSelect: (d: Date) => void;
  short?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(cursor.getFullYear());

  // Reset the picker's year to whatever's currently shown each time it opens,
  // rather than remembering wherever it was left after a prior open/close.
  useEffect(() => {
    if (open) setYear(cursor.getFullYear());
  }, [open, cursor]);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={cx(
            "inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-line bg-surface px-2 text-sm font-extrabold tracking-tight text-ink transition-colors hover:border-line-strong",
            short ? "h-[26px] min-w-[76px]" : "h-7 min-w-[130px]",
          )}
        >
          {(short ? MONTHS_SHORT : MONTHS_LONG)[cursor.getMonth()]} {cursor.getFullYear()}
        </button>
      }
      align="end"
      panelClassName="w-56 rounded-xl border border-line bg-surface shadow-lg"
    >
      <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
        <CompactIconButton label="Previous year" onClick={() => setYear((y) => y - 1)}>
          <IconChevronLeft size={12} />
        </CompactIconButton>
        <span className="mono text-xs font-bold text-ink">{year}</span>
        <CompactIconButton label="Next year" onClick={() => setYear((y) => y + 1)}>
          <IconChevronRight size={12} />
        </CompactIconButton>
      </div>
      <div className="grid grid-cols-3 gap-1 p-2">
        {MONTHS_SHORT.map((m, i) => (
          <button
            key={m}
            type="button"
            onClick={() => {
              onSelect(new Date(year, i, 1));
              setOpen(false);
            }}
            className={cx(
              "rounded-md px-2 py-1.5 text-xs font-semibold",
              i === cursor.getMonth() && year === cursor.getFullYear()
                ? "bg-accent text-bg"
                : "text-ink-soft hover:bg-surface-2",
            )}
          >
            {m}
          </button>
        ))}
      </div>
    </Popover>
  );
}
